import { itemsApi } from "#/api/itemsApi";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import { Button, Modal, Text } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface BulkDeleteModalProps {
  opened: boolean;
  onClose: () => void;
  selectedItemIds: number[];
  onSuccess: () => void;
}

const BulkDeleteModal = ({
  opened,
  onClose,
  selectedItemIds,
  onSuccess,
}: BulkDeleteModalProps) => {
  const { t } = useTranslation();
  const { canDelete } = usePermission();

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      const response = await itemsApi.bulkDelete(ids);
      if (!response.data) {
        throw new Error("Failed to delete items");
      }
      return response.data;
    },
    onSuccess: (data) => {
      showSuccessNotification({
        message: t("items.bulkDelete.success", { count: data.deleted }),
      });
      onClose();
      onSuccess();
    },
    onError: (error: unknown) => {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || t("items.bulkDelete.error");
      showErrorNotification({
        message: errorMessage,
      });
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={t("items.bulkDelete.title")}
      closeOnClickOutside={false}
      size="md"
      classNames={{
        title: "font-bold",
      }}
    >
      <div className="w-full flex flex-col gap-4">
        <Text size="sm" c="dimmed">
          {t("items.bulkDelete.description", {
            count: selectedItemIds.length,
          })}
        </Text>

        <Text size="sm" c="red" fw={500}>
          {t("items.bulkDelete.warning")}
        </Text>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            color="black"
            onClick={onClose}
            disabled={bulkDeleteMutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <PermissionTooltip disabled={!canDelete()}>
            <Button
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={() => bulkDeleteMutation.mutate(selectedItemIds)}
              loading={bulkDeleteMutation.isPending}
              disabled={!canDelete()}
            >
              {t("items.bulkDelete.confirmButton", {
                count: selectedItemIds.length,
              })}
            </Button>
          </PermissionTooltip>
        </div>
      </div>
    </Modal>
  );
};

export default BulkDeleteModal;
